import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { teacherGrades, teacherSubjectsForGrade } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight, GraduationCap, BookOpen, Users, Map, FileText, Layers, ClipboardList } from "lucide-react";
import TeacherNotifBell from "@/components/TeacherNotifBell";
import { Link } from "react-router-dom";
import TestGeneratorModal from "@/components/TestGeneratorModal";
import TeacherTestResults from "@/components/TeacherTestResults";
import StudentListPanel from "@/components/StudentListPanel";

const SUBJECT_TINT = {
  Mathematics: { bg: "bg-sky-50",     border: "border-sky-200",     text: "text-sky-800",     accent: "text-sky-600"     },
  English:     { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   accent: "text-amber-600"   },
  Hindi:       { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-800",    accent: "text-rose-600"    },
  EVS:         { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", accent: "text-emerald-600" },
};

function formatLiveDateTime(date) {
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
  return `${weekday}, ${day} ${month} · ${time}`;
}

/**
 * Teacher Home — sequential Grade → Subject tile selectors.
 * On Subject pick, navigates to /teacher/cockpit/:grade/:subject.
 */
export default function TeacherDashboard() {
  const { teacherId } = useRole();
  const { grade: gradeParam } = useParams(); // set when mounted via /teacher/cockpit/:grade
  const nav = useNavigate();

  const [grades, setGrades] = useState([]);
  const [liveTime, setLiveTime] = useState(() => formatLiveDateTime(new Date()));
  const clockRef = useRef(null);

  useEffect(() => {
    clockRef.current = setInterval(() => setLiveTime(formatLiveDateTime(new Date())), 1000);
    return () => clearInterval(clockRef.current);
  }, []);
  // Pre-select grade from URL param if present (subject-picker deep-link)
  const [grade, setGrade] = useState(() => gradeParam ? parseInt(gradeParam, 10) : null);
  const [activeTab, setActiveTab] = useState("cockpit"); // "cockpit" | "results"

  // Sync local grade state when gradeParam changes (same component reused across /teacher/cockpit/:grade routes)
  useEffect(() => {
    if (gradeParam) {
      setGrade(parseInt(gradeParam, 10));
    }
  }, [gradeParam]);
  const [subjects, setSubjects] = useState([]);
  const [fmModuleAllowed, setFmModuleAllowed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showTestGenerator, setShowTestGenerator] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showStudentList, setShowStudentList] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await teacherGrades(teacherId);
        const list = data?.grades || [];
        setGrades(list);
        // Auto-select if exactly one grade
        if (list.length === 1) setGrade(list[0].grade);
      } finally { setLoading(false); }
    })();
  }, [teacherId]);

  useEffect(() => {
    if (grade == null) { setSubjects([]); return; }
    (async () => {
      const data = await teacherSubjectsForGrade(teacherId, grade);
      setSubjects(data?.subjects || []);
      setFmModuleAllowed(data?.fm_module_allowed !== false);
    })();
  }, [teacherId, grade]);
  
  // Listen for test generator open event from header
  useEffect(() => {
    const handleOpen = () => {
      setSelectedSubject(null);
      setShowTestGenerator(true);
    };
    window.addEventListener('openTestGenerator', handleOpen);
    return () => window.removeEventListener('openTestGenerator', handleOpen);
  }, []);

  const heading = useMemo(() => {
    if (grade == null) return "Pick a grade to begin";
    if (activeTab === "results") return `Grade ${grade} — test results`;
    return `Grade ${grade} — pick a subject`;
  }, [grade, activeTab]);

  return (
    <div className="teacher-scope min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="teacher-home">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-teacher text-3xl md:text-4xl font-bold text-slate-900" data-testid="teacher-home-title">
              Teacher cockpit
            </h1>
            <p className="text-slate-500 text-sm mt-0.5 tabular-nums" data-testid="live-clock-teacher">{liveTime}</p>
            <p className="text-slate-600 mt-1">{heading}</p>
          </div>
          <div className="flex items-center gap-2">
            {grade != null && (
              <Button
                onClick={() => setShowStudentList(true)}
                variant="outline"
                size="sm"
                data-testid="students-btn-dashboard"
                className="rounded-lg border-slate-300 hover:border-slate-900"
              >
                <Users className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Students</span>
              </Button>
            )}
            <TeacherNotifBell teacherId={teacherId} />
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab("cockpit")}
            className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition border-b-2 ${
              activeTab === "cockpit"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-cockpit"
          >
            Class Cockpit
          </button>
          <button
            onClick={() => setActiveTab("results")}
            className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg transition border-b-2 flex items-center gap-1.5 ${
              activeTab === "results"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-test-results"
          >
            <ClipboardList className="w-4 h-4" /> Test Results
          </button>
        </div>

        {/* Test Results tab */}
        {activeTab === "results" && (
          <TeacherTestResults initialGrade={grade} />
        )}

        {/* Cockpit tab */}
        {activeTab === "cockpit" && (<>

        {/* Breadcrumb / reset */}
        {grade != null && grades.length > 1 && (
          <div className="text-xs text-slate-500 mb-4" data-testid="teacher-home-breadcrumb">
            <button
              data-testid="teacher-home-back-to-grades"
              onClick={() => { setGrade(null); if (gradeParam) nav("/teacher"); }}
              className="hover:underline font-semibold text-slate-700"
            >
              ← Back to grades
            </button>
            <span className="mx-2">·</span>
            <span>Grade {grade}</span>
          </div>
        )}

        {/* Stage 1 — Grade picker */}
        {grade == null && (
          <div data-testid="grade-picker">
            {loading ? (
              <div className="text-slate-400 py-12 text-center">Loading…</div>
            ) : grades.length === 0 ? (
              <Card className="rounded-xl p-8 text-center text-slate-500 border border-slate-200 bg-white">
                No learners assigned to this teacher yet.
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {grades.map(g => (
                  <button
                    key={g.grade}
                    data-testid={`grade-tile-${g.grade}`}
                    onClick={() => nav(`/teacher/cockpit/${g.grade}`)}
                    className="group rounded-2xl p-6 bg-white border-2 border-slate-200 hover:border-slate-900 hover:shadow-md text-left transition"
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                        <GraduationCap className="w-6 h-6" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-700 transition" />
                    </div>
                    <div className="mt-4 font-teacher text-2xl font-bold text-slate-900">Grade {g.grade}</div>
                    <div className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                      <Users className="w-4 h-4" /> {g.learner_count} learner{g.learner_count === 1 ? "" : "s"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stage 2 — Subject picker (for the chosen grade) */}
        {grade != null && (
          <div data-testid="subject-picker">
            {subjects.length === 0 ? (
              <Card className="rounded-xl p-8 text-center text-slate-500 border border-slate-200 bg-white">
                No subjects available for grade {grade}.
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Math Foundations — always first, gated by school's subject whitelist */}
                {fmModuleAllowed && (
                <div>
                  <button
                    data-testid="subject-tile-Math Foundations"
                    onClick={() => nav(`/teacher/fm-panel/${grade}`)}
                    className="group rounded-2xl p-6 bg-fuchsia-50 border-2 border-fuchsia-200 hover:shadow-md text-left transition w-full"
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-white/70 flex items-center justify-center text-fuchsia-600">
                        <Layers className="w-6 h-6" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-fuchsia-400 opacity-50 group-hover:opacity-100 transition" />
                    </div>
                    <div className="mt-4 font-teacher text-2xl font-bold text-fuchsia-800">Math Foundations</div>
                    <div className="text-sm text-slate-600 mt-1">Number Skills · All Grades</div>
                    <div className="text-xs text-fuchsia-600 font-semibold mt-1">
                      View learner skill states &amp; unlock
                    </div>
                  </button>
                </div>
                )}

                {subjects.map(s => {
                  const tint = SUBJECT_TINT[s.subject] || SUBJECT_TINT.Mathematics;
                  return (
                    <div key={s.subject}>
                      <button
                        data-testid={`subject-tile-${s.subject}`}
                        onClick={() => nav(`/teacher/cockpit/${grade}/${encodeURIComponent(s.subject)}`)}
                        className={`group rounded-2xl p-6 ${tint.bg} border-2 ${tint.border} hover:shadow-md text-left transition w-full`}
                      >
                        <div className="flex items-start justify-between">
                          <div className={`w-12 h-12 rounded-xl bg-white/70 flex items-center justify-center ${tint.accent}`}>
                            <BookOpen className="w-6 h-6" />
                          </div>
                          <ChevronRight className={`w-5 h-5 ${tint.accent} opacity-50 group-hover:opacity-100 transition`} />
                        </div>
                        <div className={`mt-4 font-teacher text-2xl font-bold ${tint.text}`}>{s.subject}</div>
                        <div className="text-sm text-slate-600 mt-1">
                          {s.chapter_count} chapters · {s.sub_topic_count} sub-topics
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {s.learners_engaged} of {s.learners_total} learners engaged
                        </div>
                      </button>
                    </div>
                  );
                })}

              </div>
            )}
          </div>
        )}
        </>) /* end cockpit tab */}
        
        {/* Test Generator Modal */}
        <TestGeneratorModal
          open={showTestGenerator}
          onClose={() => {
            setShowTestGenerator(false);
            setSelectedSubject(null);
          }}
          teacherId={teacherId}
          grade={grade}
          preSelectedSubject={selectedSubject}
        />

        {/* Student List Panel — grade-level context (no subject) */}
        {grade != null && (
          <StudentListPanel
            open={showStudentList}
            onClose={() => setShowStudentList(false)}
            teacherId={teacherId}
            grade={grade}
          />
        )}
      </div>
    </div>
  );
}
